import React, {
    FunctionComponent,
    PropsWithChildren,
    useState,
    useEffect
} from "react";
import { graphql, navigate, useStaticQuery } from "gatsby";
import {
    Navigation,
    NavigationAdjust,
    MenuItem,
    Product
} from "@episerver/platform-navigation";
import {
    ContentArea,
    Workspace,
    ContextualToolbar,
    WorkItemNavigation,
    Anchor,
    Typography,
    ListDivider
} from "@episerver/ui-framework";
import {
    mapMenuItems,
    getSelectedMenuItemsByLocation,
    getAspectNavigationItemsByLocation
} from "../util/menuUtils";
import { ItemNavigation } from "./item-navigation";
import { MDXProvider } from "@mdx-js/react";
import SEO from "./seo";

// Shortcodes
import { ColorSwatch as Color } from "./color-swatch";
import { ComponentProps } from "./component-props";
import { Column, Row } from "./grid";
import { DemoUnavailable } from "./demo-unavailable";
import { DeprecationWarning } from "./deprecation-warning";
import { Do, Dont, Caution } from "./caption";
import { ExternalImage } from "./external-image";
import { ExternalVideo } from "./external-video";
import { IATable } from "./information-architecture-table";
import { Storybook } from "./storybook";

import "./layout.scss";
import "@episerver/ui-framework/dist/fonts.css";
import "@episerver/ui-framework/dist/main.css";
import "@episerver/platform-navigation/dist/main.css";

const layoutQuery = graphql`
    query {
        allMdx(
            filter: {
                parent: { internal: { type: { eq: "File" } } }
                fields: { topLevelDir: { title: { ne: "" } } }
            }
        ) {
            nodes {
                fields {
                    slug
                    subDir {
                        title
                        url
                    }
                    topLevelDir {
                        title
                        url
                    }
                }
                frontmatter {
                    title
                }
                tableOfContents
            }
        }
        site {
            siteMetadata {
                title
            }
        }
    }
`;

interface TableOfContentsItem {
    title: string;
    url: string;
    items: TableOfContentsItem[];
}

export interface LayoutData {
    allMdx: {
        nodes: {
            fields: {
                slug: string;
                subDir: { title: string; url: string };
                topLevelDir: { title: string; url: string };
            };
            frontmatter: {
                title: string;
            };
            tableOfContents: { items: TableOfContentsItem[] };
        }[];
    };
    site: {
        siteMetadata: {
            title: string;
        };
    };
}

interface LayoutProps {
    pageContext: {
        frontmatter: {
            title: string;
        };
    };
    location?: {
        pathname: string;
        hash: string;
    };
}

interface ExtendedMenuItem extends MenuItem {
    workItems: MenuItem[];
}

const TypographyH1: FunctionComponent = (props) => <Typography use="headline1" tag="h1" className="prose" {...props} />;

const shortcodes = {
    Color,
    Column,
    Row,
    Do,
    Dont,
    Caution,
    ComponentProps,
    DemoUnavailable,
    DeprecationWarning,
    ExternalImage,
    ExternalVideo,
    IATable,
    Storybook,
    h1: TypographyH1,
    /* eslint-disable react/display-name */
    h2: (props: any) => <Typography use="headline2" tag="h2" className="prose" {...props} />,
    h3: (props: any) => <Typography use="headline3" tag="h3" className="prose" {...props} />,
    h4: (props: any) => <Typography use="headline4" tag="h4" className="prose" {...props} />,
    h5: (props: any) => <Typography use="headline5" tag="h5" className="prose" {...props} />,
    h6: (props: any) => <Typography use="headline6" tag="h6" className="prose" {...props} />,
    p: (props: any) => <Typography use="body1" tag="p" className="prose" {...props} />,
    ul: (props: any) => <Typography use="body1" tag="ul" className="prose" {...props} />,
    ol: (props: any) => <Typography use="body1" tag="ol" className="prose" {...props} />,
    table: (props: any) => <Typography use="body1" tag="table" className="prose epi-ds-site-table" {...props} />,
    a: (props: any) => <Anchor className="prose" {...props} />
    /* eslint-enable */
};

const Layout: FunctionComponent<PropsWithChildren<LayoutProps>> = ({
    pageContext,
    children,
    location
}: PropsWithChildren<LayoutProps>) => {
    const [levelOne, setLevelOne] = useState<MenuItem>();
    const [levelTwo, setLevelTwo] = useState<MenuItem>();
    const [workItems, setWorkItems] = useState<MenuItem[]>([]);
    const [selectedWorkItemIndex, setSelectedWorkItemIndex] = useState(0);
    const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
    const [product, setProduct] = useState<Product>({ name: "", url: "" });
    const [previousLocation, setPreviousLocation] = useState("");
    const [aspectNavigationItems, setAspectNavigationItems] = useState<MenuItem[]>([]);

    const data: LayoutData = useStaticQuery(layoutQuery);

    const updateSelectedMenuItems = (
        menuItems: MenuItem[],
        forceNewSelection: boolean
    ): void => {
        if (
            (forceNewSelection && location) ||
            (location && location.pathname !== previousLocation)
        ) {
            const selectedMenuItems = getSelectedMenuItemsByLocation(
                location.pathname,
                menuItems
            );

            setLevelOne(selectedMenuItems.levelOne);
            setLevelTwo(selectedMenuItems.levelTwo);
            setWorkItems(selectedMenuItems.workItems);
            setSelectedWorkItemIndex(
                selectedMenuItems.workItems.indexOf(selectedMenuItems.workItem)
            );
            setPreviousLocation(location.pathname);
            setAspectNavigationItems(
                getAspectNavigationItemsByLocation(data, location.pathname)
            );
        }
    };

    useEffect(() => {
        const initialMenuItems = mapMenuItems(data);

        const designSystem = {
            name: data.site.siteMetadata.title,
            url: "/"
        };

        setMenuItems(initialMenuItems);
        setProduct(designSystem);
        updateSelectedMenuItems(initialMenuItems, true);
    }, []);

    updateSelectedMenuItems(menuItems, false);

    // Don't render the normal page layout for the full-size demo
    if (location && location.pathname.includes("full-size-navigation-demo")) {
        return <>{children}</>;
    }

    return (
        <>
            <SEO title={(pageContext && pageContext.frontmatter && pageContext.frontmatter.title) || ""} />
            <Navigation
                menuItems={menuItems}
                product={product}
                levelOne={levelOne}
                levelTwo={levelTwo}
                onItemSelect={(selectedLevelOne, selectedLevelTwo) => {
                    if (selectedLevelTwo) {
                        navigate(selectedLevelTwo.url);
                    } else {
                        navigate((selectedLevelOne as ExtendedMenuItem).workItems[0].url);
                    }
                }}
                // The DS site doesn't have a login, so there's no authenticatedUserId or accountId to send
                // Instead, we'll add a custom boolean property 'user_external' which will denote whether the track event
                // is from the public version of the DS site or the internal version.
                // Disable eslint since our customProperties keys follow a different naming convention
                // eslint-disable-next-line @typescript-eslint/camelcase
                telemetry={{ customProperties: { user_external: false } }}
            />
            <NavigationAdjust>
                <ContentArea>
                    <WorkItemNavigation>
                        <ItemNavigation
                            items={workItems}
                            selectedIndex={selectedWorkItemIndex}
                            handleSelect={selectedIndex => {
                                setSelectedWorkItemIndex(selectedIndex);
                                navigate(workItems[selectedIndex].url);
                            }}
                        />
                    </WorkItemNavigation>
                    <Workspace>
                        {pageContext && pageContext.frontmatter && (
                            <TypographyH1>{pageContext.frontmatter.title}</TypographyH1>
                        )}
                        <MDXProvider components={shortcodes}>
                            {children}
                        </MDXProvider>
                        <footer className="epi-ds-site-footer_legal">
                            <ListDivider tag="div" />
                            <Typography use="body2" tag="div">
                                Some written content and images are sourced from the <Anchor use="body2" href="https://material.io/">Material.io website</Anchor> on 2018&ndash;2020.
                                Some of the design components featured in the system are based on Material.io downloads based on the <Anchor use="body2" href="https://www.apache.org/licenses/LICENSE-2.0.html">Apache 2.0</Anchor> and <Anchor use="body2" href="https://creativecommons.org/licenses/by/4.0/legalcode">CC-BY 4.0</Anchor> licensing.
                            </Typography>
                        </footer>
                    </Workspace>
                    { aspectNavigationItems.length > 0 && <ContextualToolbar>
                        <ItemNavigation aspectNavigation items={aspectNavigationItems} />
                    </ContextualToolbar>}
                </ContentArea>
            </NavigationAdjust>
        </>
    );
};

export default Layout;
